import { useEffect, useState, type AnimationEvent as ReactAnimationEvent, type FormEvent as ReactFormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { platformApi } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-error';
import { PageLoading } from '@/components/ui/loading';
import { KeyRound, Copy, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { nameRule, emailRule, optionalPhoneRule, optionalText, organizationCodeRule, newPasswordRule, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';

const baseSchema = z.object({
  name: nameRule('Organization Name', 200),
  email: emailRule,
  phone: optionalPhoneRule,
  address: optionalText(500),
  organizationCode: organizationCodeRule,
});

const createSchema = baseSchema.extend({
  adminName: nameRule('Admin Name', 200),
  adminEmail: emailRule,
  adminPassword: z.union([z.literal(''), newPasswordRule]),
});

type CreateFormData = z.infer<typeof createSchema>;

// Browser autofill writes values straight into the DOM, bypassing react-hook-form
// state. Normalize the organization code (trim + uppercase) exactly like the
// schema does; every other value is synced as-is and normalized by its rule.
function normalizeAutofilledValue(name: keyof CreateFormData, value: string): string {
  return name === 'organizationCode' ? value.trim().toUpperCase() : value;
}

export default function OrganizationFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const isEdit = !!id;

  const [credentials, setCredentials] = useState<{ organizationCode: string; adminEmail: string; adminPassword: string } | null>(null);

  const { register, handleSubmit, reset, setValue, getValues, setError, formState: { errors } } = useForm<CreateFormData>({ resolver: zodResolver(isEdit ? baseSchema : createSchema) });

  const syncAutofilledValue = (event: ReactAnimationEvent<HTMLInputElement>) => {
    const animation = event.animationName;
    if (!/autofill|auto-?fill/i.test(animation)) return;
    const input = event.currentTarget;
    const name = input.name as keyof CreateFormData;
    if (!(name in (isEdit ? baseSchema : createSchema).shape)) return;
    setValue(name, normalizeAutofilledValue(name, input.value), { shouldValidate: false });
  };

  const handleFormSubmit = (event: ReactFormEvent<HTMLFormElement>) => {
    const shape = (isEdit ? baseSchema : createSchema).shape;
    for (const name of Object.keys(shape) as (keyof CreateFormData)[]) {
      if (getValues(name)) continue;
      const input = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (input && input.value) {
        setValue(name, normalizeAutofilledValue(name, input.value), { shouldValidate: false });
      }
    }
    void handleSubmit(onSubmit, onInvalid)(event);
  };

  const onInvalid = (errs: FieldErrors<CreateFormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = (d: CreateFormData) => {
    const payload: any = {
      name: d.name,
      email: d.email,
      phone: d.phone || undefined,
      address: d.address || undefined,
      organizationCode: d.organizationCode,
    };
    if (!isEdit) {
      payload.adminName = d.adminName;
      payload.adminEmail = d.adminEmail;
      payload.adminPassword = d.adminPassword || undefined;
    }
    mutation.mutate(payload);
  };

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['platform', 'organization', id],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: any }>(`/platform/organizations/${id}`);
      return r.data;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) reset({
      name: existing.name,
      email: existing.email || '',
      phone: existing.phone || '',
      address: existing.address || '',
      organizationCode: existing.organization_code,
    });
  }, [existing, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => isEdit
      ? platformApi.patch(`/platform/organizations/${id}`, data)
      : platformApi.post('/platform/organizations', data),
    onSuccess: (res: any) => {
      if (!isEdit && res?.credentials) {
        setCredentials(res.credentials);
        addToast('Organization created. Save the initial admin credentials.', 'success');
      } else {
        addToast(`Organization ${isEdit ? 'updated' : 'created'}`, 'success');
        navigate('/super-admin/organizations');
      }
    },
    onError: (err: any) => handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) }),
  });

  const copyCredentials = async () => {
    if (!credentials) return;
    const text = `Organization ID: ${credentials.organizationCode}\nAdmin Email: ${credentials.adminEmail}\nTemporary Password: ${credentials.adminPassword}`;
    try {
      await navigator.clipboard.writeText(text);
      addToast('Credentials copied to clipboard', 'success');
    } catch {
      addToast('Could not copy credentials', 'error');
    }
  };

  const downloadCredentials = () => {
    if (!credentials) return;
    const text = `Organization ID: ${credentials.organizationCode}\nAdmin Email: ${credentials.adminEmail}\nTemporary Password: ${credentials.adminPassword}\n\nThis password is temporary and must be changed on first login.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${credentials.organizationCode}-admin-credentials.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isEdit && loadingExisting) return <PageLoading />;

  if (credentials) {
    return (
      <div className="max-w-xl mx-auto space-y-6">
        <h1 className="text-2xl font-heading font-bold text-primary">Organization Created</h1>
        <Card className="border-success">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><KeyRound size={18} /> Initial Admin Credentials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-secondary">
              These credentials are shown once. The temporary password must be changed on first login.
            </p>
            <div className="rounded-lg border border-default bg-surface-secondary p-4 space-y-2 font-mono text-sm min-w-0">
              <p className="break-all"><span className="text-secondary">Organization ID:</span> <span className="font-semibold">{credentials.organizationCode}</span></p>
              <p className="break-all"><span className="text-secondary">Admin Email:</span> <span className="font-semibold">{credentials.adminEmail}</span></p>
              <p className="break-all"><span className="text-secondary">Temporary Password:</span> <span className="font-semibold">{credentials.adminPassword}</span></p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Button variant="outline" className="gap-2 w-full sm:w-auto" onClick={copyCredentials}><Copy size={14} /> Copy</Button>
              <Button variant="outline" className="gap-2 w-full sm:w-auto" onClick={downloadCredentials}><Download size={14} /> Download</Button>
              <Button className="w-full sm:w-auto sm:ml-auto" onClick={() => navigate('/super-admin/organizations')}>Done</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Link to="/super-admin/organizations" className="text-sm text-secondary hover:underline">Organizations</Link>
        <span className="text-secondary">/</span>
        <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit Organization' : 'New Organization'}</h1>
      </div>

      <form onSubmit={handleFormSubmit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Organization Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4">
            <div>
              <Label>Organization ID (Code) *</Label>
              <Input
                {...register('organizationCode')}
                placeholder="e.g. SWAYAM001"
                autoComplete="organization"
                onAnimationStart={syncAutofilledValue}
                className={cn('font-mono', errors.organizationCode && 'border-error')}
                aria-invalid={errors.organizationCode ? true : undefined}
                aria-describedby={errors.organizationCode ? 'organizationCode-error' : undefined}
              />
              <FormError id="organizationCode-error" message={errors.organizationCode?.message} />
            </div>
            <div>
              <Label>Organization Name *</Label>
              <Input
                {...register('name')}
                placeholder="e.g. Swayam Solutions"
                autoComplete="organization"
                onAnimationStart={syncAutofilledValue}
                aria-invalid={errors.name ? true : undefined}
                aria-describedby={errors.name ? 'name-error' : undefined}
                className={cn(errors.name && 'border-error')}
              />
              <FormError id="name-error" message={errors.name?.message} />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                {...register('email')}
                placeholder="org@company.com"
                autoComplete="email"
                onAnimationStart={syncAutofilledValue}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={cn(errors.email && 'border-error')}
              />
              <FormError id="email-error" message={errors.email?.message} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                {...register('phone')}
                placeholder="Optional"
                aria-invalid={errors.phone ? true : undefined}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
                className={cn(errors.phone && 'border-error')}
              />
              <FormError id="phone-error" message={errors.phone?.message} />
            </div>
            <div>
              <Label>Address</Label>
              <Input {...register('address')} placeholder="Optional" />
            </div>
          </CardContent>
        </Card>

        {!isEdit && (
          <Card>
            <CardHeader><CardTitle className="text-base">Initial Administrator</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 gap-4">
              <div>
                <Label>Admin Name *</Label>
                <Input
                  {...register('adminName')}
                  placeholder="e.g. Rahul Sharma"
                  autoComplete="name"
                  onAnimationStart={syncAutofilledValue}
                  aria-invalid={errors.adminName ? true : undefined}
                  aria-describedby={errors.adminName ? 'adminName-error' : undefined}
                  className={cn(errors.adminName && 'border-error')}
                />
                <FormError id="adminName-error" message={errors.adminName?.message} />
              </div>
              <div>
                <Label>Admin Email *</Label>
                <Input
                  type="email"
                  {...register('adminEmail')}
                  placeholder="admin@company.com"
                  autoComplete="email"
                  onAnimationStart={syncAutofilledValue}
                  aria-invalid={errors.adminEmail ? true : undefined}
                  aria-describedby={errors.adminEmail ? 'adminEmail-error' : undefined}
                  className={cn(errors.adminEmail && 'border-error')}
                />
                <FormError id="adminEmail-error" message={errors.adminEmail?.message} />
              </div>
              <div>
                <Label>Temporary Password</Label>
                <Input
                  type="text"
                  {...register('adminPassword')}
                  placeholder="Leave blank to auto-generate a secure password"
                  className={cn('font-mono', errors.adminPassword && 'border-error')}
                  aria-invalid={errors.adminPassword ? true : undefined}
                  aria-describedby={errors.adminPassword ? 'adminPassword-error' : undefined}
                />
                <FormError id="adminPassword-error" message={errors.adminPassword?.message} />
                <p className="text-xs text-muted mt-1">The initial admin will be forced to change this password on first login.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => navigate('/super-admin/organizations')}>Cancel</Button>
          <Button type="submit" className="w-full sm:w-auto" loading={mutation.isPending}>{isEdit ? 'Update Organization' : 'Create Organization'}</Button>
        </div>
      </form>
    </div>
  );
}
